import React from "react";
import {
  Navbar,
  Nav,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Card,
  Form,
} from "react-bootstrap";
import {
  PeopleFill,
  Award,
  Briefcase,
  Diagram3Fill,
  BarChartFill,
  ChatDotsFill,
  ChevronRight,
  Linkedin,
  Twitter,
  Envelope,
} from "react-bootstrap-icons";

const AWSLeadershipTeamLandingPage_2025_12_09: React.FC = () => {
  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div style={{ backgroundColor: "#050816", color: "#f8f9fa", minHeight: "100vh" }}>
      {/* Navbar */}
      <Navbar
        expand="lg"
        variant="dark"
        bg="dark"
        sticky="top"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
      >
        <Container>
          <Navbar.Brand
            onClick={() => scrollToId("top")}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
          >
            <Diagram3Fill className="me-2" color="#FF9900" />
            <span style={{ fontWeight: 700 }}>AWS Leadership Team</span>
            <Badge bg="warning" text="dark" className="ms-2">
              Advisory
            </Badge>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="aws-leadership-team-navbar" />
          <Navbar.Collapse
            id="aws-leadership-team-navbar"
            className="justify-content-end"
          >
            <Nav className="me-3">
              <Nav.Link onClick={() => scrollToId("about")}>About</Nav.Link>
              <Nav.Link onClick={() => scrollToId("services")}>Services</Nav.Link>
              <Nav.Link onClick={() => scrollToId("team")}>Team</Nav.Link>
              <Nav.Link onClick={() => scrollToId("approach")}>Approach</Nav.Link>
              <Nav.Link onClick={() => scrollToId("contact")}>Contact</Nav.Link>
              <Nav.Link onClick={() => scrollToId("faq")}>FAQ</Nav.Link>
            </Nav>
            <Button
              variant="warning"
              className="fw-semibold"
              onClick={() => scrollToId("contact")}
            >
              Schedule Intro Call <ChevronRight className="ms-1" />
            </Button>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero */}
      <section id="top" style={{ padding: "4.5rem 0 4rem 0" }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={7} className="mb-4 mb-lg-0">
              <Badge bg="secondary" className="mb-3">
                Fractional & embedded AWS leadership
              </Badge>
              <h1 className="display-5 fw-bold mb-3">
                Build an{" "}
                <span style={{ color: "#FF9900" }}>AWS-ready leadership team</span>{" "}
                that can scale with you.
              </h1>
              <p className="lead text-light mb-4" style={{ maxWidth: 640 }}>
                AWS Leadership Team brings seasoned cloud leaders to help you design
                strategy, coach managers, and level up your AWS organization—without
                the overhead of hiring a full-time executive bench.
              </p>

              <div className="d-flex flex-wrap gap-3 mb-3">
                <Button
                  size="lg"
                  variant="warning"
                  className="fw-semibold"
                  onClick={() => scrollToId("contact")}
                >
                  Book a Discovery Call
                </Button>
                <Button
                  size="lg"
                  variant="outline-light"
                  onClick={() => scrollToId("services")}
                >
                  Explore Engagement Models
                </Button>
              </div>

              <div className="d-flex flex-wrap gap-4 mt-3 text-muted">
                <div>
                  <span className="fw-semibold text-light">Fractional Leaders</span>
                  <span className="d-block" style={{ fontSize: "0.875rem" }}>
                    CxO &amp; Head-of roles
                  </span>
                </div>
                <div>
                  <span className="fw-semibold text-light">Manager Coaching</span>
                  <span className="d-block" style={{ fontSize: "0.875rem" }}>
                    Grow your internal bench
                  </span>
                </div>
                <div>
                  <span className="fw-semibold text-light">Team Alignment</span>
                  <span className="d-block" style={{ fontSize: "0.875rem" }}>
                    Strategy, culture &amp; delivery
                  </span>
                </div>
              </div>
            </Col>

            <Col lg={5}>
              <Card
                bg="dark"
                text="light"
                className="shadow-lg border-0"
                style={{
                  borderRadius: 20,
                  background:
                    "linear-gradient(145deg, rgba(10, 25, 47, 0.95), rgba(7, 15, 29, 0.98))",
                }}
              >
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <BarChartFill color="#FF9900" size={24} className="me-2" />
                    <Card.Title className="mb-0 fw-semibold">
                      AWS leadership, on your side of the table
                    </Card.Title>
                  </div>
                  <p className="text-light small mb-3">
                    Partner with a team that understands leadership, culture, and AWS.
                    We help you connect cloud investment with clear business outcomes.
                  </p>
                  <ul className="small text-muted mb-3">
                    <li>Clarify your AWS vision and operating model</li>
                    <li>Coach managers &amp; tech leads to lead with confidence</li>
                    <li>Align product, engineering, and business around shared goals</li>
                  </ul>
                  <Button
                    variant="warning"
                    className="w-100 fw-semibold mb-2"
                    onClick={() => scrollToId("contact")}
                  >
                    Download Overview Deck (PDF)
                  </Button>
                  <small className="d-block text-center text-muted">
                    Replace with a real link or modal when your assets are ready.
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* About */}
      <section id="about" style={{ padding: "3rem 0" }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="fw-bold mb-3">Who is AWS Leadership Team for?</h2>
              <p className="text-light">
                We work with founders, CTOs, and executives who need experienced AWS
                leadership—but aren&apos;t ready or able to build an entire executive
                layer in-house.
              </p>
              <p className="text-light">
                From early-stage teams shipping their first AWS workloads to
                established organizations modernizing legacy systems, we help you
                design an operating model, grow leaders, and keep teams aligned as you
                scale.
              </p>
            </Col>
            <Col lg={6}>
              <Row xs={1} md={3} className="g-3">
                <Col>
                  <Card
                    bg="dark"
                    text="light"
                    className="h-100 border-0"
                    style={{ borderRadius: 16 }}
                  >
                    <Card.Body className="text-center">
                      <Award size={28} color="#FF9900" className="mb-2" />
                      <Card.Title as="h6" className="mb-1">
                        Proven Leaders
                      </Card.Title>
                      <Card.Text className="small text-muted">
                        Leadership experience across AWS-heavy orgs.
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card
                    bg="dark"
                    text="light"
                    className="h-100 border-0"
                    style={{ borderRadius: 16 }}
                  >
                    <Card.Body className="text-center">
                      <Briefcase size={28} color="#FF9900" className="mb-2" />
                      <Card.Title as="h6" className="mb-1">
                        Operator Mindset
                      </Card.Title>
                      <Card.Text className="small text-muted">
                        Hands-on with the realities of delivery &amp; budgets.
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col>
                  <Card
                    bg="dark"
                    text="light"
                    className="h-100 border-0"
                    style={{ borderRadius: 16 }}
                  >
                    <Card.Body className="text-center">
                      <ChatDotsFill size={28} color="#FF9900" className="mb-2" />
                      <Card.Title as="h6" className="mb-1">
                        Collaborative
                      </Card.Title>
                      <Card.Text className="small text-muted">
                        We embed, listen, coach, and co-create with your team.
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services / Engagement Models */}
      <section
        id="services"
        style={{
          padding: "3rem 0 3.5rem 0",
          background:
            "radial-gradient(circle at top, rgba(255,153,0,0.18), transparent 60%)",
        }}
      >
        <Container>
          <Row className="mb-4">
            <Col className="text-center">
              <h2 className="fw-bold mb-2">Ways we can work together</h2>
              <p className="text-muted mb-0">
                Choose the engagement model that matches your stage, budget, and goals.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center mb-2">
                    <Badge bg="warning" text="dark" className="me-2">
                      01
                    </Badge>
                    Fractional AWS Leadership
                  </Card.Title>
                  <Card.Text className="small text-muted mb-2">
                    Part-time leadership (e.g. Fractional CTO, VP Eng, Head of Cloud)
                    focused on AWS strategy and execution.
                  </Card.Text>
                  <ul className="small text-muted mb-0">
                    <li>1–3 days per week embedded with your team</li>
                    <li>Strategic guidance + hands-on decision support</li>
                    <li>Ideal for high-growth teams</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center mb-2">
                    <Badge bg="warning" text="dark" className="me-2">
                      02
                    </Badge>
                    Leadership Pods
                  </Card.Title>
                  <Card.Text className="small text-muted mb-2">
                    A small team of AWS-focused leaders (strategy + coaching + delivery
                    oversight) working in sync with your org.
                  </Card.Text>
                  <ul className="small text-muted mb-0">
                    <li>Cross-functional leadership coverage</li>
                    <li>Coach managers &amp; tech leads in real time</li>
                    <li>Great for complex AWS programs</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center mb-2">
                    <Badge bg="warning" text="dark" className="me-2">
                      03
                    </Badge>
                    Advisory &amp; Coaching
                  </Card.Title>
                  <Card.Text className="small text-muted mb-2">
                    Targeted advisory calls, workshops, and leadership coaching focused
                    on AWS org design, culture, and decision-making.
                  </Card.Text>
                  <ul className="small text-muted mb-0">
                    <li>Monthly or quarterly advisory cadence</li>
                    <li>1:1 and small group coaching</li>
                    <li>Perfect for strengthening your internal bench</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Team Highlights */}
      <section id="team" style={{ padding: "3rem 0" }}>
        <Container>
          <Row className="mb-4">
            <Col className="text-center">
              <h2 className="fw-bold mb-2">Your AWS leadership partners</h2>
              <p className="text-muted mb-0">
                Example profiles you can replace with your real team bios.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0 text-center">
                <Card.Body>
                  <PeopleFill size={40} color="#FF9900" className="mb-3" />
                  <Card.Title className="mb-1">Alex Morgan</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted small">
                    Fractional CTO &amp; AWS Architect
                  </Card.Subtitle>
                  <Card.Text className="small text-muted">
                    15+ years leading engineering teams, with deep experience in
                    serverless, data platforms, and large-scale AWS migrations.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0 text-center">
                <Card.Body>
                  <PeopleFill size={40} color="#FF9900" className="mb-3" />
                  <Card.Title className="mb-1">Jordan Lee</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted small">
                    VP of Engineering Advisor
                  </Card.Subtitle>
                  <Card.Text className="small text-muted">
                    Specialist in org design, performance management, and leadership
                    coaching for fast-growing AWS-centric companies.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0 text-center">
                <Card.Body>
                  <PeopleFill size={40} color="#FF9900" className="mb-3" />
                  <Card.Title className="mb-1">Riley Patel</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted small">
                    Product &amp; Cloud Strategy Partner
                  </Card.Subtitle>
                  <Card.Text className="small text-muted">
                    Bridges product, business, and AWS architecture to keep teams
                    aligned on outcomes and value, not just features.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Approach / How We Work */}
      <section
        id="approach"
        style={{
          padding: "3rem 0 3.5rem 0",
          backgroundColor: "#020712",
          borderTop: "1px solid rgba(255,255,255,0.07)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <Container>
          <Row className="mb-4">
            <Col className="text-center">
              <h2 className="fw-bold mb-2">How we partner with your team</h2>
              <p className="text-muted mb-0">
                A simple, collaborative process you can adapt to your context.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title className="mb-2">1. Listen &amp; Diagnose</Card.Title>
                  <Card.Text className="small text-muted">
                    We interview key leaders, review your AWS landscape, and map out
                    current challenges across people, process, and technology.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title className="mb-2">2. Co-Design the Plan</Card.Title>
                  <Card.Text className="small text-muted">
                    Together we define leadership priorities, engagement scope, and
                    clear success metrics tied to business outcomes.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title className="mb-2">3. Embed &amp; Uplift</Card.Title>
                  <Card.Text className="small text-muted">
                    We embed alongside your team, lead by example, and coach your
                    leaders so capability remains long after our engagement.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact / Lead Capture */}
      <section id="contact" style={{ padding: "3.5rem 0" }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="fw-bold mb-2">Let&apos;s explore if we&apos;re a fit</h2>
              <p className="text-light mb-3">
                Share a bit about your team and AWS context, and we&apos;ll follow up
                with options for a no-pressure discovery call.
              </p>
              <ul className="text-muted small mb-0">
                <li>Typical response within 1–2 business days.</li>
                <li>We&apos;ll send a short agenda before any call.</li>
                <li>If we&apos;re not the right fit, we&apos;ll say so honestly.</li>
              </ul>
            </Col>
            <Col lg={6}>
              <Card
                bg="dark"
                text="light"
                className="shadow border-0"
                style={{ borderRadius: 18 }}
              >
                <Card.Body>
                  <Card.Title className="mb-3">
                    Request a discovery conversation
                  </Card.Title>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      // Replace with your real handler / API call
                      alert(
                        "Thanks for reaching out to AWS Leadership Team! Connect this form to your backend, CRM, or email service."
                      );
                    }}
                  >
                    <Row className="g-3">
                      <Col sm={6}>
                        <Form.Group controlId="awsLeadershipTeamName">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control type="text" placeholder="Your name" required />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="awsLeadershipTeamEmail">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            placeholder="you@company.com"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-3 mt-1">
                      <Col sm={6}>
                        <Form.Group controlId="awsLeadershipTeamRole">
                          <Form.Label>Role</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. Founder, CTO, VP Eng"
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group controlId="awsLeadershipTeamCompany">
                          <Form.Label>Company</Form.Label>
                          <Form.Control type="text" placeholder="Company name" />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group controlId="awsLeadershipTeamStage" className="mt-3">
                      <Form.Label>Company stage</Form.Label>
                      <Form.Select defaultValue="Startup (1-50)">
                        <option value="Startup (1-50)">Startup (1–50 people)</option>
                        <option value="Growth (51-250)">Growth (51–250 people)</option>
                        <option value="Scale (251-1000)">Scale (251–1000 people)</option>
                        <option value="Enterprise (1000+)">Enterprise (1000+)</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="awsLeadershipTeamContext" className="mt-3">
                      <Form.Label>
                        What&apos;s happening with your AWS organization right now?
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="E.g. rapid growth, migration, transformation, leadership gaps, alignment issues..."
                      />
                    </Form.Group>

                    <Form.Group controlId="awsLeadershipTeamConsent" className="mt-3">
                      <Form.Check
                        type="checkbox"
                        label="I agree to be contacted about AWS Leadership Team services."
                        required
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="warning"
                      className="mt-3 w-100 fw-semibold"
                    >
                      Submit Request
                    </Button>
                    <small className="d-block text-center text-muted mt-2">
                      We&apos;ll only use your info to follow up on this request.
                    </small>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: "3rem 0 2rem 0" }}>
        <Container>
          <Row className="mb-4">
            <Col className="text-center">
              <h2 className="fw-bold mb-2">Frequently Asked Questions</h2>
              <p className="text-muted mb-0">
                Tailor these answers to your actual offer, pricing, and logistics.
              </p>
            </Col>
          </Row>
          <Row className="g-3">
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title as="h6" className="mb-2">
                    How do you price engagements?
                  </Card.Title>
                  <Card.Text className="small text-muted">
                    We typically price based on engagement model (fractional, pod, or
                    advisory), time commitment, and complexity. After an initial
                    discovery, we&apos;ll share clear options and ranges so there are no
                    surprises.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title as="h6" className="mb-2">
                    Do you work on-site or remotely?
                  </Card.Title>
                  <Card.Text className="small text-muted">
                    Many engagements are remote-first with the option of periodic
                    on-site visits for key workshops, strategy sessions, or planning
                    offsites, depending on geography and scope.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card bg="dark" text="light" className="h-100 border-0">
                <Card.Body>
                  <Card.Title as="h6" className="mb-2">
                    Are you affiliated with AWS?
                  </Card.Title>
                  <Card.Text className="small text-muted">
                    AWS Leadership Team is an independent advisory offering focused on
                    leadership in AWS-powered organizations. It is not owned, operated,
                    or endorsed by Amazon Web Services, Inc.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: "1.5rem 0 1.75rem 0",
          backgroundColor: "#02040b",
          borderTop: "1px solid rgba(255,255,255,0.07)",
          fontSize: "0.875rem",
        }}
      >
        <Container>
          <Row className="align-items-center gy-2">
            <Col md={6}>
              <span className="text-muted">
                © {new Date().getFullYear()} AWS Leadership Team. Independent advisory
                for AWS-focused organizations.
              </span>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end justify-content-start">
              <div className="d-flex align-items-center gap-3">
                <a
                  href="mailto:hello@example.com"
                  aria-label="Email AWS Leadership Team"
                  className="text-muted"
                  onClick={(e) => e.preventDefault()}
                >
                  <Envelope />
                </a>
                <a
                  href="#"
                  aria-label="AWS Leadership Team LinkedIn"
                  className="text-muted"
                  onClick={(e) => e.preventDefault()}
                >
                  <Linkedin />
                </a>
                <a
                  href="#"
                  aria-label="AWS Leadership Team Twitter"
                  className="text-muted"
                  onClick={(e) => e.preventDefault()}
                >
                  <Twitter />
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default AWSLeadershipTeamLandingPage_2025_12_09;
